import SeoHead from '@presentation/seo/SeoHead'

type LegalSection = {
  title: string
  paragraphs: string[]
}

const PRIVACY_POLICY_SECTIONS: LegalSection[] = [
  {
    title: 'Introduction',
    paragraphs: [
      'Parkinson\'s Insights ("we," "us," or "our") is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your personal data when you visit our website or engage with our collaboration (PD Buddy and No Silver Bullet 4PD). We comply with the UK General Data Protection Regulation (UK GDPR) and relevant US privacy laws.',
    ],
  },
  {
    title: 'Data Controller',
    paragraphs: [
      'The Data Controller for Parkinson\'s Insights is Good Vibes Technologies Ltd.',
    ],
  },
  {
    title: 'Information We Collect',
    paragraphs: [
      'We collect information that allows us to provide a better service to our community, including:',
      'Personal Identifiers: Name and email address provided when signing up for newsletters, the PD Buddy app, or webinars.',
      'Health Data: Information regarding your journey with Parkinson\'s that you voluntarily share through app usage or community forums. This is classified as "Special Category Data" and is handled with the highest level of security.',
      'Technical Data: IP addresses and cookies used for website functionality and analytics to understand how our audience interacts with our stories.',
    ],
  },
  {
    title: 'Sovereign AI and Data Privacy',
    paragraphs: [
      'As part of our commitment to cutting-edge support, we utilise Sovereign AI agents to help users navigate information and personalise their experience.',
      'Data Minimisation: Our AI agents are designed to process only the information necessary to answer your queries.',
      'Privacy by Design: Unlike many public AI models, our Sovereign AI approach ensures that your personal health data is not used to "train" external, third-party models. Your data remains within our secure environment.',
      'Anonymisation: Where data is used to improve the intelligence of our internal systems, it is strictly anonymised to ensure that no individual can be identified.',
    ],
  },
  {
    title: 'How We Use Your Data',
    paragraphs: [
      'We use your information to:',
      'Provide and maintain the PD Buddy app and website services.',
      'Send you educational content, webinar invitations, and updates.',
      'Improve our holistic strategies based on anonymised community trends.',
      'Comply with legal obligations.',
    ],
  },
  {
    title: 'Data Sharing and Storage',
    paragraphs: [
      'We do not sell your personal or health data to third parties. Data may be shared with trusted service providers who assist us in operating our platform, provided they agree to keep this information confidential. For our US audience, your data may be processed on servers located outside your home country. We ensure that appropriate safeguards are in place to protect your data according to UK and EU standards.',
    ],
  },
  {
    title: 'Your Rights',
    paragraphs: [
      'Depending on your location, you have the right to access the personal data we hold about you, request corrections, or request the deletion of your data. You may withdraw your consent at any time by clicking \'unsubscribe\' in our emails.',
    ],
  },
  {
    title: 'Contact Us',
    paragraphs: [
      'If you have any questions regarding your privacy or wish to exercise your data rights, please contact us at: support@goodvibestechnologies.com.',
    ],
  },
]

export default function PrivacyPolicyPage() {
  return (
    <main className="bg-background py-8 sm:py-12">
      <SeoHead
        title="Privacy Policy | Parkinson's Insights"
        description="Review how Parkinson's Insights collects, uses, and protects personal data across the website and related services."
        canonicalPath="/legal/privacy-policy"
      />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-border-light bg-surface p-6 shadow-sm sm:p-8">
          <header className="border-b border-border-light pb-5 sm:pb-6">
            <h1 className="text-2xl font-bold tracking-tight text-primary sm:text-3xl">
              Privacy Policy
            </h1>
            <p className="mt-2 text-sm font-medium text-text-secondary">
              Last Updated: 14 March 2026
            </p>
          </header>

          {PRIVACY_POLICY_SECTIONS.map((section) => (
            <section
              key={section.title}
              className="border-b border-border-light py-5 last:border-b-0 last:pb-0 sm:py-6"
            >
              <h2 className="text-xl font-bold tracking-tight text-primary sm:text-2xl">
                {section.title}
              </h2>
              <div className="mt-4 space-y-4">
                {section.paragraphs.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="text-base leading-relaxed text-text-primary"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </main>
  )
}
